<template>
  <div class="watermark-wrapper" ref="containerRef">
    <!-- 插槽用于放置被水印覆盖的内容 -->
    <slot></slot>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

// 定义组件的属性
const props = defineProps({
  content: { type: String, required: true }, // 水印文字
  rotate: { type: Number, default: -22 }, // 旋转角度
  zIndex: { type: Number, default: 9999 }, // 层级
  gap: { type: Array, default: () => [200, 100] }, // 水印间距 [水平, 垂直]
  textStyle: { // 文字样式
    type: Object,
    default: () => ({
      color: 'rgba(0, 0, 0, 0.03)',
      fontSize: 12,
      fontWeight: 'normal',
      fontFamily: 'sans-serif'
    })
  }
})

const containerRef = ref(null)
const watermarkElement = ref(null)

const generateWatermark22 = () => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const ratio = window.devicePixelRatio || 1

  const [gapX, gapY] = props.gap
  
  // 设置Canvas物理像素尺寸
  canvas.width = gapX * ratio
  canvas.height = gapY * ratio
  
  // 设置Canvas CSS尺寸
  canvas.style.width = `${gapX}px`
  canvas.style.height = `${gapY}px`

  // 缩放上下文，确保绘制内容清晰
  ctx.scale(ratio, ratio)
  
  // 现在绘制时使用原始尺寸
  ctx.font = `${props.textStyle.fontWeight} ${props.textStyle.fontSize}px ${props.textStyle.fontFamily}`
  ctx.fillStyle = props.textStyle.color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // 这里使用原始gap值，而不是除以ratio
  ctx.translate(gapX / 2, gapY / 2)
  ctx.rotate((Math.PI / 180) * props.rotate)
  ctx.fillText(props.content, 0, 0)

  return canvas.toDataURL('image/png')
}

// 生成水印的Base64图片数据
const generateWatermark = () => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const ratio = window.devicePixelRatio || 1 // 适配高清屏

  // 动态计算画布大小
  const [gapX, gapY] = props.gap
  const canvasWidth = (gapX * ratio) 
  const canvasHeight = (gapY * ratio)
  canvas.width = canvasWidth
  canvas.height = canvasHeight

  // 设置绘制样式
  // ctx.scale(ratio, ratio)
  ctx.font = `${props.textStyle.fontWeight} ${props.textStyle.fontSize}px ${props.textStyle.fontFamily}`
  ctx.fillStyle = props.textStyle.color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // 旋转并绘制文字
  ctx.translate(canvasWidth / ratio / 2, canvasHeight / ratio / 2)
  ctx.rotate((Math.PI / 180) * props.rotate)
  ctx.fillText(props.content, 0, 0)


  return canvas.toDataURL('image/png')
}

// 渲染水印到页面
const renderWatermark = () => {
  if (!containerRef.value) return

  // 如果已存在水印元素，先移除
  if (watermarkElement.value) {
    watermarkElement.value.remove()
  }

  // 创建新的水印元素
  watermarkElement.value = document.createElement('div')
  watermarkElement.value.className = 'watermark-layer'
  watermarkElement.value.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: ${props.zIndex};
    background-image: url('${generateWatermark()}');
    background-repeat: repeat;
  `

  containerRef.value.style.position = 'relative'
  containerRef.value.appendChild(watermarkElement.value)
}

// 监听属性变化，更新水印
watch(() => [props.content, props.rotate, props.gap, props.textStyle], renderWatermark, { deep: true })

onMounted(renderWatermark)
onBeforeUnmount(() => {
  if (watermarkElement.value) {
    watermarkElement.value.remove()
  }
})
</script>

<style scoped>
.watermark-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>